import React, { useEffect, useRef, useState } from 'react';
import { KeyRound, RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useWS } from '../WSContext';

// API kimliklerinin geçerlilik paneli.
//
// Neden var: bazı borsaların API cüzdanları süreli ve dolduğunda borsa imzayı
// sessizce reddediyor. Aster'da tam bunu yaşadık — hata "No agent found" diye
// göründü, bir kod arızası sanıldı. Kalan günü göz önünde tutmak bu sürprizi
// tümden bitiriyor.
//
// KULLANDIĞIMIZ kimlik vurgulanır: bir hesapta birden çok API cüzdanı olabilir
// ve kullanmadığımız birinin dolması bizi ilgilendirmiyor. Onları soluk gösterip
// uyarıyı yalnız bizimkine bağlıyoruz, yoksa panel sürekli kırmızı yanar.

interface ApiKey {
  venue: string;
  botId: string;
  name: string;
  address: string;
  expiresAt: number;
  daysLeft: number;
  isOurs: boolean;
  canWithdraw: boolean;
  canTrade: boolean;
}

const WARN_DAYS = 30;
const URGENT_DAYS = 7;

export default function ApiKeyPanel({ dark = true }: { dark?: boolean }) {
  const { sendWs, subscribe } = useWS();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [busy, setBusy] = useState(false);
  const asked = useRef(false);

  useEffect(() => {
    const unsub = subscribe((m: any) => {
      if (m.type === 'api_keys') {
        setKeys(m.payload?.keys || []);
        setBusy(false);
      }
    });
    if (!asked.current) {
      asked.current = true;
      sendWs({ type: 'get_api_keys', payload: {} });
    }
    return () => unsub?.();
  }, [subscribe, sendWs]);

  const refresh = () => {
    setBusy(true);
    sendWs({ type: 'get_api_keys', payload: { refresh: true } });
  };

  const card   = dark ? 'bg-[#061530]/80 border-[#0a2560]' : 'bg-white border-[#d4e0f0]';
  const muted  = dark ? 'text-blue-400' : 'text-slate-500';
  const rowSub = dark ? 'border-[#0a2560]' : 'border-[#e8eef7]';

  const tone = (k: ApiKey) => {
    if (!k.isOurs) return muted;
    if (k.daysLeft <= 0)          return 'text-rose-400';
    if (k.daysLeft <= URGENT_DAYS) return 'text-rose-400';
    if (k.daysLeft <= WARN_DAYS)   return 'text-amber-400';
    return 'text-emerald-400';
  };

  const gun = (d: number) =>
    d <= 0 ? 'DOLDU' : d < 1 ? '<1 gün' : `${Math.floor(d)} gün`;

  const tarih = (ms: number) =>
    ms > 0 ? new Date(ms).toLocaleDateString('tr-TR') : '—';

  // Uyarı yalnız BİZİM kimliğe bakar.
  const riskli = keys.filter(k => k.isOurs && k.daysLeft <= WARN_DAYS);

  return (
    <div className={`rounded-xl border ${card} p-3`}>
      <div className="flex items-center gap-2 mb-2">
        <KeyRound size={14} className="text-blue-400" />
        <span className="text-xs font-semibold">API kimlikleri</span>
        {riskli.length > 0 && (
          <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
            <AlertTriangle size={10} />
            {riskli.length} kimlik yenilenmeli
          </span>
        )}
        <button onClick={refresh} disabled={busy}
          className={`ml-auto ${muted} hover:text-blue-300 disabled:opacity-40`}
          title="Borsalardan yeniden sor">
          <RefreshCw size={13} className={busy ? 'animate-spin' : ''} />
        </button>
      </div>

      {keys.length === 0 ? (
        <div className={`text-[11px] ${muted} py-2`}>
          {busy ? 'Sorgulanıyor…' : 'Süre bildiren kimlik yok.'}
        </div>
      ) : (
        <div className="space-y-1">
          {keys.map((k, i) => (
            <div key={`${k.venue}-${k.address}-${i}`}
                 className={`flex items-center gap-2 text-[11px] py-1 ${i ? `border-t ${rowSub}` : ''} ${k.isOurs ? '' : 'opacity-45'}`}>
              <span className="font-medium w-[74px] shrink-0">{k.venue}</span>
              <span className="truncate max-w-[110px]" title={k.name}>{k.name}</span>
              {k.isOurs && (
                <span className="flex items-center gap-0.5 text-[9px] px-1 py-px rounded bg-blue-500/15 text-blue-300 shrink-0">
                  kullanılan
                </span>
              )}
              {k.isOurs && !k.canWithdraw && (
                <span className={`flex items-center gap-0.5 text-[9px] ${muted} shrink-0`}
                      title="Bu kimlik para çekemez — yalnız işlem yetkisi var">
                  <ShieldCheck size={9} /> çekim yok
                </span>
              )}
              <span className={`ml-auto font-mono ${muted} shrink-0`}>{tarih(k.expiresAt)}</span>
              <span className={`font-mono font-semibold w-[58px] text-right shrink-0 ${tone(k)}`}>
                {gun(k.daysLeft)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
